/**
 * QuestionBankEditor — lists a proctored interview's generated question
 * bank and lets the interviewer edit or delete any question, and manually
 * add new ones. AI generation and manual authoring aren't exclusive: a
 * recruiter can generate a batch with "Generate Question Bank" in the
 * wizard and then hand-tune individual questions here, or skip AI entirely
 * and build the whole bank by hand.
 */

import React, { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { PencilIcon, TrashIcon, PlusIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  createQuestion, updateQuestion, deleteQuestion,
  type ProctoredQuestion, type SkillRubric, type TestCase,
} from '@/services/proctoredInterviewApi'
import { type QuestionType } from '@/services/questionTypeApi'
import { type ComplexityLevel } from '@/services/complexityLevelApi'

interface QuestionBankEditorProps {
  configId: string
  questions: ProctoredQuestion[]
  skillsRubrics: SkillRubric[]
  questionTypes: QuestionType[]
  complexityLevels?: ComplexityLevel[]
  // The Skills & Rubrics editor above has edits not yet saved. Adding a
  // question reloads that editor from the last save, so we warn first.
  hasUnsavedSkillChanges?: boolean
}

// A coding question must have at least one test case with an expected
// output — without one there's nothing for the candidate's "Run Tests" or
// for scoring to check against. Input may be empty (a program that reads
// nothing from stdin).
const CODING_TEST_CASE_REQUIRED_MESSAGE = 'Add at least one test case for a coding question.'
const hasUsableTestCase = (testCases: TestCase[]) =>
  testCases.some((tc) => (tc.expected_output || '').trim() !== '')

function typeName(questionTypes: QuestionType[], typeId: string, subtypeId?: string | null): string {
  const type = questionTypes.find((t) => t.id === typeId)
  if (!type) return 'Unknown type'
  const subtype = subtypeId ? type.subtypes.find((s) => s.id === subtypeId) : null
  return subtype ? `${type.name} · ${subtype.name}` : type.name
}

function levelName(levels: ComplexityLevel[], levelId?: string | null): string | null {
  return levelId ? levels.find((l) => l.id === levelId)?.name || null : null
}

export default function QuestionBankEditor({
  configId, questions, skillsRubrics, questionTypes, complexityLevels = [], hasUnsavedSkillChanges = false,
}: QuestionBankEditorProps) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  // Shown when Add Question is clicked while Skills & Rubrics has unsaved
  // edits. Adding is blocked until they're saved — there's no "add anyway".
  const [showSaveFirst, setShowSaveFirst] = useState(false)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['proctored-interview-config'] })

  const updateMutation = useMutation({
    mutationFn: (vars: { questionId: string; data: Parameters<typeof updateQuestion>[2] }) =>
      updateQuestion(configId, vars.questionId, vars.data),
    onSuccess: () => { toast.success('Question updated'); setEditingId(null); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to update question'),
  })

  const deleteMutation = useMutation({
    mutationFn: (questionId: string) => deleteQuestion(configId, questionId),
    onSuccess: () => { toast.success('Question deleted'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to delete question'),
  })

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof createQuestion>[1]) => createQuestion(configId, data),
    onSuccess: () => { toast.success('Question added'); setShowAddForm(false); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to add question'),
  })

  const sorted = useMemo(() => [...questions].sort((a, b) => a.display_order - b.display_order), [questions])

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Question Bank</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Every candidate for this job sees these questions. Edit, remove, or add one manually.
          </p>
        </div>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            disabled={skillsRubrics.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50"
            title={skillsRubrics.length === 0 ? 'Add a skill first' : undefined}
          >
            <PlusIcon className="w-3.5 h-3.5" /> Add Question
          </button>
        )}
      </div>

      {showAddForm && (
        <AddQuestionForm
          skillsRubrics={skillsRubrics}
          questionTypes={questionTypes}
          complexityLevels={complexityLevels}
          submitting={createMutation.isPending}
          onCancel={() => setShowAddForm(false)}
          onSubmit={(data) => {
            if (hasUnsavedSkillChanges) {
              setShowSaveFirst(true)
              return
            }
            createMutation.mutate(data)
          }}
        />
      )}

      {showSaveFirst && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-skills-title"
          onClick={() => setShowSaveFirst(false)}
        >
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 id="unsaved-skills-title" className="text-base font-semibold text-gray-900">Unsaved changes</h3>
                <p className="text-sm text-gray-600 mt-1.5">
                  Click <span className="font-medium text-gray-800">Save Progress</span> before adding a question.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowSaveFirst(false)}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                autoFocus
              >
                OK, go back
              </button>
            </div>
          </div>
        </div>
      )}

      {sorted.length === 0 && !showAddForm ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">No questions yet. Generate with AI or add one manually.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {sorted.map((q) => (
            <QuestionRow
              key={q.id}
              question={q}
              typeLabel={[typeName(questionTypes, q.question_type_id, q.question_subtype_id), levelName(complexityLevels, q.complexity_level_id)]
                .filter(Boolean).join(' · ')}
              isCoding={questionTypes.find((t) => t.id === q.question_type_id)?.category === 'coding'}
              rubricOptions={skillsRubrics.find((s) => s.skill_name === q.skill_name)?.rubric_criteria.map((c) => c.name) || []}
              isEditing={editingId === q.id}
              saving={updateMutation.isPending}
              deleting={deleteMutation.isPending}
              onEdit={() => setEditingId(q.id)}
              onCancelEdit={() => setEditingId(null)}
              onSave={(data) => updateMutation.mutate({ questionId: q.id, data })}
              onDelete={() => {
                if (window.confirm('Remove this question from the bank?')) deleteMutation.mutate(q.id)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Question row (view + inline edit)
// ============================================================================

function QuestionRow({
  question, typeLabel, isCoding, rubricOptions, isEditing, saving, deleting, onEdit, onCancelEdit, onSave, onDelete,
}: {
  question: ProctoredQuestion
  typeLabel: string
  isCoding: boolean
  rubricOptions: string[]
  isEditing: boolean
  saving: boolean
  deleting: boolean
  onEdit: () => void
  onCancelEdit: () => void
  onSave: (data: { question_text: string; guidance_notes?: string; duration_minutes: number; rubric_criteria_covered: string[]; test_cases: TestCase[] }) => void
  onDelete: () => void
}) {
  const [text, setText] = useState(question.question_text)
  const [notes, setNotes] = useState(question.guidance_notes || '')
  const [duration, setDuration] = useState(question.duration_minutes)
  const [criteria, setCriteria] = useState<string[]>(question.rubric_criteria_covered)
  const [testCases, setTestCases] = useState<TestCase[]>(question.test_cases || [])

  if (!isEditing) {
    return (
      <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{question.skill_name}</span>
              <span className="text-xs text-gray-400">{typeLabel}</span>
              <span className="text-xs text-gray-400">· {question.duration_minutes} min</span>
            </div>
            <p className="text-sm text-gray-900">{question.question_text}</p>
            {question.rubric_criteria_covered.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">Covers: {question.rubric_criteria_covered.join(', ')}</p>
            )}
            {isCoding && question.test_cases.length > 0 && (
              <div className="mt-2 space-y-1">
                {question.test_cases.map((tc, idx) => (
                  <p key={idx} className="text-xs font-mono text-gray-500 bg-gray-50 rounded px-2 py-1">
                    in: {tc.input} → out: {tc.expected_output}
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit">
              <PencilIcon className="w-4 h-4" />
            </button>
            <button onClick={onDelete} disabled={deleting} className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-50" title="Delete">
              {deleting ? <LoadingSpinner size="sm" /> : <TrashIcon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-5 py-4 bg-gray-50 space-y-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{question.skill_name}</span>
        <span className="text-xs text-gray-400">{typeLabel}</span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        placeholder="Question text"
      />
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs"
        placeholder="Guidance notes for scoring (not shown to candidate)"
      />
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Duration (min)</label>
        <input
          type="number" min={1} max={180}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value) || 1)}
          className="w-16 border border-gray-300 rounded px-2 py-1 text-xs text-center"
        />
      </div>
      {isCoding && <TestCasesEditor testCases={testCases} onChange={setTestCases} />}
      {rubricOptions.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Rubric criteria this question covers</p>
          <div className="flex flex-wrap gap-2">
            {rubricOptions.map((name) => {
              const checked = criteria.includes(name)
              return (
                <label key={name} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${checked ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-600'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setCriteria(checked ? criteria.filter((c) => c !== name) : [...criteria, name])}
                    className="hidden"
                  />
                  {name}
                </label>
              )
            })}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={() => {
            if (isCoding && !hasUsableTestCase(testCases)) {
              toast.error(CODING_TEST_CASE_REQUIRED_MESSAGE)
              return
            }
            onSave({ question_text: text, guidance_notes: notes || undefined, duration_minutes: duration, rubric_criteria_covered: criteria, test_cases: testCases })
          }}
          disabled={saving || !text.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <LoadingSpinner size="sm" /> : <CheckIcon className="w-3.5 h-3.5" />} Save
        </button>
        <button onClick={onCancelEdit} className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50">
          <XMarkIcon className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Test cases editor (coding questions only) — shared by row-edit and add-form
// ============================================================================

function TestCasesEditor({ testCases, onChange }: { testCases: TestCase[]; onChange: (tc: TestCase[]) => void }) {
  const update = (idx: number, field: keyof TestCase, value: string) => {
    onChange(testCases.map((tc, i) => (i === idx ? { ...tc, [field]: value } : tc)))
  }
  const remove = (idx: number) => onChange(testCases.filter((_, i) => i !== idx))
  const add = () => onChange([...testCases, { input: '', expected_output: '', description: '' }])

  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-1">Test Cases</p>
      <div className="space-y-2">
        {testCases.map((tc, idx) => (
          <div key={idx} className="border border-gray-200 rounded-md p-2 space-y-1 bg-white">
            <div className="flex gap-2 items-start">
              <input
                value={tc.input}
                onChange={(e) => update(idx, 'input', e.target.value)}
                placeholder="Input"
                className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs font-mono"
              />
              <input
                value={tc.expected_output}
                onChange={(e) => update(idx, 'expected_output', e.target.value)}
                placeholder="Expected output"
                className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs font-mono"
              />
              <button onClick={() => remove(idx)} className="text-gray-300 hover:text-red-500 mt-1 flex-shrink-0">
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <input
              value={tc.description || ''}
              onChange={(e) => update(idx, 'description', e.target.value)}
              placeholder="What this case checks (optional)"
              className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
            />
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-1.5 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
        <PlusIcon className="w-3 h-3" /> Add test case
      </button>
    </div>
  )
}

// ============================================================================
// Manual "Add Question" form
// ============================================================================

function AddQuestionForm({
  skillsRubrics, questionTypes, complexityLevels, submitting, onCancel, onSubmit,
}: {
  skillsRubrics: SkillRubric[]
  questionTypes: QuestionType[]
  complexityLevels: ComplexityLevel[]
  submitting: boolean
  onCancel: () => void
  onSubmit: (data: {
    skill_name: string
    question_type_id: string
    question_subtype_id?: string | null
    complexity_level_id?: string | null
    question_text: string
    guidance_notes?: string
    duration_minutes?: number
    rubric_criteria_covered: string[]
    test_cases: TestCase[]
  }) => void
}) {
  const [skillName, setSkillName] = useState(skillsRubrics[0]?.skill_name || '')
  const [typeId, setTypeId] = useState(questionTypes[0]?.id || '')
  const [subtypeId, setSubtypeId] = useState<string>('')
  const [levelId, setLevelId] = useState<string>('')
  const [text, setText] = useState('')
  const [notes, setNotes] = useState('')
  const [duration, setDuration] = useState<number | ''>('')
  const [criteria, setCriteria] = useState<string[]>([])
  const [testCases, setTestCases] = useState<TestCase[]>([])

  const selectedType = questionTypes.find((t) => t.id === typeId)
  const isCoding = selectedType?.category === 'coding'
  const rubricOptions = skillsRubrics.find((s) => s.skill_name === skillName)?.rubric_criteria.map((c) => c.name) || []

  const handleTypeChange = (id: string) => {
    setTypeId(id)
    setSubtypeId('')
  }

  const handleSubmit = () => {
    if (!skillName || !typeId || !text.trim()) {
      toast.error('Skill, question type, and question text are required.')
      return
    }
    if (isCoding && !hasUsableTestCase(testCases)) {
      toast.error(CODING_TEST_CASE_REQUIRED_MESSAGE)
      return
    }
    onSubmit({
      skill_name: skillName,
      question_type_id: typeId,
      question_subtype_id: subtypeId || null,
      complexity_level_id: levelId || null,
      question_text: text,
      guidance_notes: notes || undefined,
      duration_minutes: duration === '' ? undefined : duration,
      rubric_criteria_covered: criteria,
      test_cases: testCases,
    })
  }

  return (
    <div className="px-5 py-4 bg-blue-50/40 border-b border-gray-100 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Skill</label>
          <select
            value={skillName}
            onChange={(e) => { setSkillName(e.target.value); setCriteria([]) }}
            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          >
            {skillsRubrics.map((s) => <option key={s.skill_name} value={s.skill_name}>{s.skill_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Question Type</label>
          <select
            value={typeId}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          >
            {questionTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {selectedType && selectedType.subtypes.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sub-type (optional)</label>
            <select
              value={subtypeId}
              onChange={(e) => setSubtypeId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">None</option>
              {selectedType.subtypes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        {complexityLevels.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Complexity (optional)</label>
            <select
              value={levelId}
              onChange={(e) => setLevelId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">Any complexity</option>
              {complexityLevels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Duration (min) — default {selectedType?.default_duration_minutes ?? 10}
          </label>
          <input
            type="number" min={1} max={180}
            value={duration}
            onChange={(e) => setDuration(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={String(selectedType?.default_duration_minutes ?? 10)}
            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Question Text</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="What should the candidate be asked?"
          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Guidance Notes (optional, not shown to candidate)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="What a strong answer should cover"
          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs"
        />
      </div>

      {isCoding && <TestCasesEditor testCases={testCases} onChange={setTestCases} />}

      {rubricOptions.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Rubric criteria this question covers</p>
          <div className="flex flex-wrap gap-2">
            {rubricOptions.map((name) => {
              const checked = criteria.includes(name)
              return (
                <label key={name} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${checked ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-600'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setCriteria(checked ? criteria.filter((c) => c !== name) : [...criteria, name])}
                    className="hidden"
                  />
                  {name}
                </label>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? <LoadingSpinner size="sm" /> : <PlusIcon className="w-3.5 h-3.5" />}
          {submitting ? 'Adding…' : 'Add Question'}
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  )
}
